import { cn } from '@/shared/utils/formatting';
import { AlertCircle, Check } from 'lucide-react';
import type { ReactNode } from 'react';

export type StatusLineState = 'idle' | 'checking' | 'ok' | 'err';

interface StatusLineProps {
  state: StatusLineState;
  /** Copy shown while nothing is happening. */
  idle: string;
  okLabel: string;
  error?: string | undefined;
}

function content(state: StatusLineState, idle: string, okLabel: string, error?: string): ReactNode {
  if (state === 'err' && error) {
    return (
      <span role="alert" className="flex items-center gap-2 text-red-primary">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {error}
      </span>
    );
  }
  if (state === 'checking') {
    return (
      <>
        <span
          aria-hidden
          className="h-1.5 w-1.5 rounded-full bg-red-primary shadow-[0_0_6px_rgba(171,0,19,0.4)]"
        />
        Checking…
      </>
    );
  }
  if (state === 'ok') {
    return (
      <>
        <Check className="h-3.5 w-3.5" aria-hidden />
        {okLabel}
      </>
    );
  }
  return idle;
}

/** One-line feedback under a code input: idle hint, pulsing "Checking…", green tick, red error. */
export function StatusLine({ state, idle, okLabel, error }: StatusLineProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex min-h-5 items-center gap-2 text-[13px] text-text-tertiary',
        state === 'ok' && 'text-success'
      )}
    >
      {content(state, idle, okLabel, error)}
    </div>
  );
}
